/**
 * JS bridge for the native AppUpdaterPlugin (Android only).
 * On web / iOS this module is a safe no-op.
 */
import { registerPlugin } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";

// Registers the native plugin; on platforms where it doesn't exist the proxy
// will throw on method calls, which we handle gracefully below.
const AppUpdater = registerPlugin("AppUpdater");

/** URL of the JSON manifest on your web server. */
export const UPDATE_CHECK_URL =
  process.env.REACT_APP_UPDATE_CHECK_URL || "https://yourwebsite.com/app/update.json";

/**
 * Fetch the remote update manifest and compare against the installed version.
 * Returns null when no update is needed or when running outside Android.
 *
 * @returns {Promise<{versionCode:number, versionName:string, apkUrl:string,
 *                    forceUpdate:boolean, releaseNotes:string}|null>}
 */
export async function checkForUpdate() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
    return null;
  }

  let manifest;
  try {
    const res = await fetch(`${UPDATE_CHECK_URL}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    manifest = await res.json();
  } catch {
    return null; // network unavailable – skip silently
  }

  let info;
  try {
    info = await App.getInfo();
  } catch {
    return null;
  }

  const installedCode = parseInt(info.build, 10);
  if (
    typeof manifest.versionCode !== "number" ||
    manifest.versionCode <= installedCode
  ) {
    return null;
  }

  return manifest;
}

/**
 * Download the APK at `url` and report progress via the `onProgress` callback.
 *
 * @param {string} url  - Remote APK URL
 * @param {(pct:number)=>void} onProgress
 * @returns {Promise<string>}  Local file path of the downloaded APK
 */
export async function downloadApk(url, onProgress) {
  // Listen for native progress events
  let listenerHandle;
  try {
    listenerHandle = await AppUpdater.addListener("downloadProgress", (ev) => {
      onProgress?.(ev.percent ?? 0);
    });
  } catch {
    // Plugin not available (web / iOS) — should never reach here in practice
    throw new Error("AppUpdater plugin unavailable");
  }

  try {
    const result = await AppUpdater.downloadApk({ url });
    return result.path;
  } finally {
    listenerHandle?.remove();
  }
}

/**
 * Trigger the Android package installer for a locally downloaded APK.
 * May throw "INSTALL_PERMISSION_REQUIRED" if the user hasn't granted the
 * install-unknown-apps permission yet (the plugin opens Settings automatically).
 *
 * @param {string} path - Local path returned by downloadApk()
 */
export async function installApk(path) {
  await AppUpdater.installApk({ path });
}
